/**
 * CitationRegistryContext — resolves `<hector-link ref="…">` tokens to targets.
 *
 * The backend hands the LLM short citation tokens (`piece-12`, `art-2286-cc`,
 * `jp-1`) instead of raw urls/file_paths, and persists the `token → target` map
 * in each assistant message's metadata (delivered on the wire as
 * `metadata.citationRegistry`). This context carries the merged registry down to
 * `HectorLink` so it can resolve a token without prop-threading through the
 * markdown renderer. An unknown token resolves to `undefined` → the link
 * degrades to plain text (never a misleading destination).
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { CitationRegistry, CitationTarget } from '@hector/shared-types';

interface CitationContextValue {
  registry: CitationRegistry;
  /**
   * Dossier figé sur l'échange. Deux lecteurs :
   * - `HectorLink` y cherche une pièce citée qui ne porte pas son propre dossier
   *   (`PieceCitation.caseId`, absent des citations anciennes) : le visualiseur
   *   retrouve le chemin par `GET /cases/{caseId}/documents`. Une citation qui
   *   porte son dossier s'ouvre dans celui-là, pas dans celui-ci ;
   * - la ligne d'outil (`ToolGroup`) y compare le dossier lu par l'outil, et ne
   *   nomme ce dernier que s'il diffère.
   *
   * STRICT : c'est le dossier de CE tour. Un tour envoyé sans dossier n'en
   * emprunte jamais un plus tard (`resolveDisplayExchangeCaseId`) — emprunter
   * ouvrirait la pièce d'un autre dossier.
   */
  caseId?: string;
  /**
   * Dossier de la CONVERSATION — une simple suggestion, jamais un droit d'accès.
   *
   * WHY ce second champ plutôt qu'un assouplissement de `caseId` : les deux
   * besoins sont opposés. Ouvrir une pièce exige le dossier exact du tour, sans
   * quoi on ouvrirait le document d'un autre dossier. Proposer une DESTINATION
   * d'enregistrement n'exige rien : c'est un pré-remplissage que l'avocat peut
   * changer, et le lui refuser parce que le tour a été envoyé hors dossier
   * l'oblige à rechoisir un dossier que la conversation affiche déjà.
   */
  conversationCaseId?: string;
  /**
   * Titre d'un dossier d'après la liste de la conversation, puis les dossiers du
   * cabinet : un connexe retiré depuis garde donc son titre. `undefined` pour un
   * dossier inconnu des deux. La ligne d'outil s'en sert pour nommer le dossier
   * que l'outil a lu.
   */
  caseTitleOf?: (caseId: string) => string | undefined;
}

const CitationRegistryContext = createContext<CitationContextValue>({ registry: {} });

export const CitationRegistryProvider = CitationRegistryContext.Provider;

/**
 * Fournit le contexte d'UN échange avec une valeur stable.
 *
 * WHY un composant : l'objet `value` posé en ligne dans la boucle des échanges
 * change à chaque rendu de `V3ChatInterface`, et re-rend alors toutes les lignes
 * d'outil et tous les liens (les lecteurs du contexte). Un `useMemo` ne peut pas
 * vivre dans la boucle ; il vit ici, une fois par échange.
 */
export function ExchangeCitationProvider({
  registry,
  caseId,
  conversationCaseId,
  caseTitleOf,
  children,
}: CitationContextValue & { children: ReactNode }) {
  const value = useMemo(
    () => ({ registry, caseId, conversationCaseId, caseTitleOf }),
    [registry, caseId, conversationCaseId, caseTitleOf],
  );
  return <CitationRegistryContext.Provider value={value}>{children}</CitationRegistryContext.Provider>;
}

/** Resolve a citation token to its target, or `undefined` if unknown/absent. */
export function useCitationTarget(ref?: string): CitationTarget | undefined {
  const { registry } = useContext(CitationRegistryContext);
  return ref ? registry[ref] : undefined;
}

/** Le dossier figé sur l'échange (cf. `CitationContextValue.caseId`), s'il en a un. */
export function useCitationCaseId(): string | undefined {
  return useContext(CitationRegistryContext).caseId;
}

/**
 * Dossier à PROPOSER comme destination d'enregistrement : celui du tour s'il en
 * a un, sinon celui de la conversation. Ne jamais l'utiliser pour ouvrir une
 * ressource — cf. le commentaire de `conversationCaseId`.
 */
export function useDestinationCaseHint(): string | undefined {
  const { caseId, conversationCaseId } = useContext(CitationRegistryContext);
  return caseId ?? conversationCaseId;
}

/**
 * Ce dont la ligne d'outil a besoin pour nommer le dossier que l'outil a lu :
 * le dossier de l'échange (qu'elle ne nomme jamais, il est à l'écran) et le
 * titre des autres d'après la liste de la conversation.
 *
 * Un échange sans dossier se compare au dossier de la conversation, comme le
 * fait `useDestinationCaseHint` : sans cela, une ligne du principal se lirait
 * comme une lecture dans un autre dossier.
 */
export function useToolLineCase(): { shownCaseId: string | undefined; caseTitleOf: (caseId: string) => string | undefined } {
  const { caseId, conversationCaseId, caseTitleOf } = useContext(CitationRegistryContext);
  return { shownCaseId: caseId ?? conversationCaseId, caseTitleOf: caseTitleOf ?? (() => undefined) };
}

/** Merge several per-message registries into one (later entries win on collision). */
export function mergeCitationRegistries(
  ...registries: Array<CitationRegistry | undefined>
): CitationRegistry {
  const merged: CitationRegistry = {};
  for (const registry of registries) {
    if (registry) Object.assign(merged, registry);
  }
  return merged;
}
